import GoogleIcon from "@mui/icons-material/Google";
import LogoutIcon from "@mui/icons-material/Logout";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useSignalReady } from "contexts/globalLoadingScreen";
import {
  getAuth,
  GoogleAuthProvider,
  linkWithPopup,
  OAuthProvider,
  signOut,
} from "firebase/auth";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { backendApi } from "src/store/api/enhancedApi";
import { isEmailLinkSignin } from "src/utils/isEmailLinkSignin";
import { useIdToken } from "react-firebase-hooks/auth";

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const getAuthProvider = () =>
  localStorage.getItem("authProvider") ?? "microsoft";

export const LinkAccountPage = () => {
  useSignalReady();

  const { t } = useTranslation();
  const auth = getAuth();
  const [user, loading] = useIdToken(auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authProvider = getAuthProvider();

  const [error, setError] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  const handleLogout = async () => {
    await signOut(auth);
    dispatch(backendApi.util.resetApiState());
    void navigate("/sign-in");
  };

  useEffect(() => {
    if (!user && !loading) {
      void navigate("/sign-in");
      return;
    }

    if (user && !isEmailLinkSignin(user)) {
      void navigate("/");
      return;
    }
  }, [user, loading, navigate]);

  const handleLinkAccount = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setIsLinking(true);
    setError(null);

    const provider =
      authProvider === "google"
        ? new GoogleAuthProvider()
        : new OAuthProvider("microsoft.com");

    if (provider instanceof OAuthProvider) {
      provider.setCustomParameters({ tenant: "common" });
    }

    try {
      await linkWithPopup(user, provider);
      await user.getIdToken(true);

      dispatch(backendApi.util.resetApiState());
      void navigate("/");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("failedToLinkAccount");
      setError(errorMessage);
      setIsLinking(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100%",
        px: { xs: 2, sm: 0 },
        display: "flex",
        alignItems: "center",
      }}
    >
      <Paper sx={{ width: "100%" }} elevation={2}>
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="stretch"
          sx={{ px: { xs: 3, sm: 8 }, pt: 4, pb: 4 }}
          spacing={3}
        >
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            {t("scrapChef")}
          </Typography>

          <Typography variant="body1" sx={{ textAlign: "center" }}>
            {t("linkAccountSetupMessage", {
              provider: capitalize(authProvider),
            })}
          </Typography>

          <LoadingButton
            variant="contained"
            onClick={() => void handleLinkAccount()}
            loading={isLinking}
            startIcon={
              authProvider === "google" ? <GoogleIcon /> : <MicrosoftIcon />
            }
            sx={{ textTransform: "none", borderRadius: 0 }}
          >
            {t("linkWithProvider", {
              provider: capitalize(authProvider),
            })}
          </LoadingButton>

          {error ? (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          ) : null}

          <Button
            variant="text"
            startIcon={<LogoutIcon />}
            onClick={() => void handleLogout()}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            {t("signOut")}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};
